import React from 'react';
import { PropTypes } from 'prop-types';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Controller, get } from 'react-hook-form';

import debounce from '@seccon/shared/utils/debounce';

import useRemoveSpaces from '../utils/removeSpaces';
import getFormControllerRules from '../utils/getFormControllerRules';

function isOptionEqualToValue (option, val) {
  if (typeof option === 'object') {
    return option.value === val.value;
  }

  return option === val;
}

/**
 * Reusable Autocomplete for use with react-hook-form.
 *
 * @param {object} control - React hook form control.
 * @param {object} formErrors - React hook form formErrors.
 * @param {string} label - Label to be used for the field as well as test id and error messages.
 * @param {string} formFieldId - Id to be used with react-hook-form.
 * @param {boolean} required - Is the field required?
 * @param {boolean} readonly - Is the field read only?
 * @param {Array} options - Options to display in the dropdown
 * @param {object} textFieldProps - Accepts all TextField props.
 * @param {string} testId - Alternative testId label to overwrite label.
 * @param {object} validationOptions - React Hook Form validation options.
 * @param {boolean} showInlineErrors - Should the field show errors in help text? (defaults to `true`)
 * @param {object} optionKeyName - Option value key name.
 * @param {object} fetchedOptions - Fetching options method.
 * @param {object} rest - Also accepts all Autocomplete props.
 */
const FormAutocomplete = ({
  control,
  formErrors,
  label,
  options,
  formFieldId,
  required,
  readonly,
  testId,
  validationOptions,
  textFieldProps,
  freeSolo,
  disableClearable = true,
  showInlineErrors = true,
  optionKeyName,
  fetchedOptions,
  onAdditionalMethod,
  ...rest
}) => {
  const testIdMemo = useRemoveSpaces(testId ?? `${ label }_Field`);
  const rules = getFormControllerRules(label, required, validationOptions);
  const modifiedHelperText = (showInlineErrors && get(formErrors, formFieldId)?.message) || textFieldProps?.helperText;
  const debouncedFetchedOptions = debounce(fetchedOptions);

  return (
    <Controller
      name={formFieldId}
      control={control}
      rules={rules}
      defaultValue=''
      render={({
        field: {
          onChange, value, ref, ...field
        }
      }) => (
        <Autocomplete
          options={options}
          value={value}
          freeSolo={freeSolo}
          defaultValue={options[0] ?? ''}
          onChange={(event, newValue) => {
            onChange(newValue ?? '');
            onAdditionalMethod?.(newValue);
          }}
          onInputChange={(event, val) => {
            if (readonly) return;
            if (fetchedOptions) debouncedFetchedOptions(val);
          }}
          disableClearable={disableClearable}
          autoHighlight
          fullWidth
          disabled={readonly}
          readOnly={readonly}
          isOptionEqualToValue={isOptionEqualToValue}
          filterOptions={(filterOptions) => filterOptions}
          renderInput={(params) => (
            <TextField
              inputRef={ref}
              error={!!get(formErrors, formFieldId)}
              sx={{ overflow: 'visible' }}
              // variant={readonly ? 'filled' : 'outlined'}
              label={label}
              required={required}
              data-testid={testIdMemo}
              {...textFieldProps}
              helperText={modifiedHelperText}
              {...params}
              disabled={readonly}
            />
          )}
          {...rest}
          {...field}
        />
      )}
    />
  );
};

FormAutocomplete.propTypes = {
  control: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  formFieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  testId: PropTypes.string,
  validationOptions: PropTypes.object,
  textFieldProps: PropTypes.object,
  freeSolo: PropTypes.bool,
  disableClearable: PropTypes.bool,
  showInlineErrors: PropTypes.bool,
  optionKeyName: PropTypes.string,
  fetchedOptions: PropTypes.func.isRequired,
  onAdditionalMethod: PropTypes.func
};

FormAutocomplete.defaultProps = {
  formErrors: null,
  required: false,
  readonly: false,
  testId: null,
  validationOptions: {},
  textFieldProps: {},
  freeSolo: false,
  disableClearable: true,
  optionKeyName: null,
  showInlineErrors: true,
  onAdditionalMethod: null
};

export default FormAutocomplete;
